import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { RISK_CATEGORIES, RISK_LIKELIHOOD, RISK_IMPACT, DEPENDENCY_TYPES } from "../lib/s1000dData";
import { AlertTriangle, Plus, Trash2, Lightbulb, GitBranch, ShieldCheck } from "lucide-react";
import RiskImporter from "./RiskImporter";
import HelpIconButton from "./HelpIconButton";

const BAND_STYLES = {
  low: { label: "LOW", cls: "bg-[#15803D] text-white" },
  medium: { label: "MED", cls: "bg-[#CA8A04] text-white" },
  high: { label: "HIGH", cls: "bg-[#EA580C] text-white" },
  critical: { label: "CRIT", cls: "bg-[#FF2400] text-white" },
};

function Pill({ band }) {
  const s = BAND_STYLES[band] || BAND_STYLES.low;
  return (
    <span className={`inline-block px-1.5 py-0.5 font-mono text-[10px] font-bold tracking-wider ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function RiskTab() {
  const { t } = useTranslation();
  const {
    estimate, risk, effectiveContingencyPercent,
    addRisk, updateRisk, removeRisk,
    addAssumption, updateAssumption, removeAssumption,
    addDependency, updateDependency, removeDependency,
    updateRiskPlanField,
  } = useEstimate();
  const plan = estimate.riskPlan;

  return (
    <section className="space-y-6" data-testid="risk-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("risk.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("risk.title")}
            <HelpIconButton anchor="ch4" testId="help-icon-risk" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("risk.description")}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="risk-headline">
          <Stat label="Risks" value={risk.count} />
          <Stat label="Max Score" value={`${risk.maxScore}/25`} accent={risk.maxScore >= 16 ? "danger" : risk.maxScore >= 10 ? "warn" : "ok"} />
          <Stat label="Exposure" value={`${(risk.exposure * 100).toFixed(0)}%`} />
          <Stat label="Suggested Bump" value={`+${risk.suggestedContingencyBump.toFixed(1)}%`} accent={risk.suggestedContingencyBump > 0 ? "blue" : "ok"} />
        </div>
      </div>

      {/* AUTO BUMP TOGGLE */}
      <RiskImporter />

      <div className="bg-white border border-gray-300 p-4" data-testid="risk-auto-bump-card">
        <div className="flex items-center gap-3 flex-wrap">
          <ShieldCheck size={16} className="text-[#002FA7]" />
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!plan.autoContingency}
              onChange={(e) => updateRiskPlanField("autoContingency", e.target.checked)}
              data-testid="risk-auto-bump-toggle"
              className="w-4 h-4 accent-[#002FA7]"
            />
            <span className="text-sm font-bold uppercase tracking-wider">
              Auto-bump contingency from risk register
            </span>
          </label>
          <div className="flex items-center gap-2 ml-auto">
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Max bump %</label>
            <input
              type="number" min="0" step="1" max="100"
              value={plan.maxAutoBumpPercent}
              onChange={(e) => updateRiskPlanField("maxAutoBumpPercent", Number(e.target.value) || 0)}
              data-testid="risk-max-bump-input"
              className="border border-gray-300 rounded-none px-2 py-1 font-mono text-sm w-20 text-right"
            />
          </div>
        </div>
        <p className="text-xs text-gray-600 mt-3 font-mono" data-testid="risk-contingency-summary">
          Base contingency {estimate.contingencyPercent || 0}% + risk uplift +{risk.suggestedContingencyBump.toFixed(1)}% = effective {effectiveContingencyPercent.toFixed(1)}%
        </p>
      </div>

      {/* RISK TABLE */}
      <div className="bg-white border border-gray-300" data-testid="risk-list">
        <div className="border-b border-gray-300 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-[#FF2400]" />
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">Risks</h3>
            <span className="text-xs text-gray-500 font-mono ml-2">
              CRIT {risk.breakdown.critical} · HIGH {risk.breakdown.high} · MED {risk.breakdown.medium} · LOW {risk.breakdown.low}
            </span>
          </div>
          <button
            onClick={addRisk}
            data-testid="risk-add-btn"
            className="bg-[#002FA7] text-white hover:bg-black rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add risk
          </button>
        </div>
        {risk.risks.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm" data-testid="risk-empty">No risks yet — add the first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
                <th className="py-2 px-3 text-left">{t("deep.riskTitle")}</th>
                <th className="py-2 px-3 text-left">{t("deep.riskCategory")}</th>
                <th className="py-2 px-3 text-center">{t("risk.likelihood")}</th>
                <th className="py-2 px-3 text-center">{t("risk.impact")}</th>
                <th className="py-2 px-3 text-center">{t("deep.riskScore")}</th>
                <th className="py-2 px-3 text-left">{t("deep.riskMitigation")}</th>
                <th className="py-2 px-3 text-left">{t("deep.riskOwner")}</th>
                <th className="py-2 px-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {risk.risks.map((r) => (
                <tr key={r.id} className="border-b border-gray-200" data-testid={`risk-row-${r.id}`}>
                  <td className="py-2 px-3">
                    <input
                      type="text" value={r.title}
                      onChange={(e) => updateRisk(r.id, "title", e.target.value)}
                      data-testid={`risk-title-${r.id}`}
                      className="border border-gray-200 rounded-none px-2 py-1 text-sm w-full"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={r.category}
                      onChange={(e) => updateRisk(r.id, "category", e.target.value)}
                      data-testid={`risk-category-${r.id}`}
                      className="border border-gray-200 rounded-none px-2 py-1 text-sm bg-white"
                    >
                      {RISK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <select
                      value={r.likelihood}
                      onChange={(e) => updateRisk(r.id, "likelihood", e.target.value)}
                      data-testid={`risk-likelihood-${r.id}`}
                      className="border border-gray-200 rounded-none px-1 py-1 font-mono text-sm bg-white w-14"
                    >
                      {RISK_LIKELIHOOD.map((l) => <option key={l.v} value={l.v}>{l.v}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <select
                      value={r.impact}
                      onChange={(e) => updateRisk(r.id, "impact", e.target.value)}
                      data-testid={`risk-impact-${r.id}`}
                      className="border border-gray-200 rounded-none px-1 py-1 font-mono text-sm bg-white w-14"
                    >
                      {RISK_IMPACT.map((l) => <option key={l.v} value={l.v}>{l.v}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <div className="inline-flex items-center gap-2">
                      <span className="font-mono font-bold">{r.score}</span>
                      <Pill band={r.band} />
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="text" value={r.mitigation || ""}
                      onChange={(e) => updateRisk(r.id, "mitigation", e.target.value)}
                      data-testid={`risk-mitigation-${r.id}`}
                      placeholder="Mitigation action…"
                      className="border border-gray-200 rounded-none px-2 py-1 text-sm w-full"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="text" value={r.ownerRole || ""}
                      onChange={(e) => updateRisk(r.id, "ownerRole", e.target.value)}
                      data-testid={`risk-owner-${r.id}`}
                      placeholder="Role"
                      className="border border-gray-200 rounded-none px-2 py-1 text-sm w-full"
                    />
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={() => removeRisk(r.id)}
                      data-testid={`risk-remove-${r.id}`}
                      className="text-gray-500 hover:text-[#FF2400]"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ASSUMPTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-300" data-testid="assumptions-card">
          <div className="border-b border-gray-300 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightbulb size={16} className="text-[#CA8A04]" />
              <h3 className="font-heading text-lg tracking-tight font-bold text-black">Assumptions</h3>
            </div>
            <button
              onClick={() => addAssumption()}
              data-testid="assumption-add-btn"
              className="bg-white border border-gray-300 hover:bg-gray-100 rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="p-4 space-y-2">
            {plan.assumptions.length === 0 && (
              <p className="text-xs text-gray-400" data-testid="assumptions-empty">No assumptions yet.</p>
            )}
            {plan.assumptions.map((a) => (
              <div key={a.id} className="flex items-center gap-2" data-testid={`assumption-row-${a.id}`}>
                <input
                  type="text" value={a.text}
                  onChange={(e) => updateAssumption(a.id, e.target.value)}
                  data-testid={`assumption-text-${a.id}`}
                  className="border border-gray-200 rounded-none px-2 py-1 text-sm flex-1"
                />
                <button
                  onClick={() => removeAssumption(a.id)}
                  data-testid={`assumption-remove-${a.id}`}
                  className="text-gray-500 hover:text-[#FF2400]"
                ><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-300" data-testid="dependencies-card">
          <div className="border-b border-gray-300 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GitBranch size={16} className="text-[#8B5CF6]" />
              <h3 className="font-heading text-lg tracking-tight font-bold text-black">Dependencies</h3>
            </div>
            <button
              onClick={addDependency}
              data-testid="dependency-add-btn"
              className="bg-white border border-gray-300 hover:bg-gray-100 rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="p-4 space-y-2">
            {plan.dependencies.length === 0 && (
              <p className="text-xs text-gray-400" data-testid="dependencies-empty">No dependencies yet.</p>
            )}
            {plan.dependencies.map((d) => (
              <div key={d.id} className="flex items-center gap-2" data-testid={`dependency-row-${d.id}`}>
                <select
                  value={d.type}
                  onChange={(e) => updateDependency(d.id, "type", e.target.value)}
                  data-testid={`dependency-type-${d.id}`}
                  className="border border-gray-200 rounded-none px-2 py-1 text-xs font-bold bg-white uppercase tracking-wider"
                >
                  {DEPENDENCY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input
                  type="text" value={d.text}
                  onChange={(e) => updateDependency(d.id, "text", e.target.value)}
                  data-testid={`dependency-text-${d.id}`}
                  className="border border-gray-200 rounded-none px-2 py-1 text-sm flex-1"
                />
                <button
                  onClick={() => removeDependency(d.id)}
                  data-testid={`dependency-remove-${d.id}`}
                  className="text-gray-500 hover:text-[#FF2400]"
                ><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, accent }) {
  const accentCls =
    accent === "danger" ? "text-[#FF2400]" :
    accent === "warn" ? "text-[#CA8A04]" :
    accent === "blue" ? "text-[#002FA7]" :
    "text-black";
  return (
    <div className="border border-gray-300 px-3 py-2 bg-white min-w-[120px]">
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</p>
      <p className={`font-mono text-xl font-bold mt-1 leading-none ${accentCls}`}>{value}</p>
    </div>
  );
}
